import React, { useCallback, useState } from 'react';
import { useHistory, Link } from 'react-router-dom';
import {
  MapContainer,
  TileLayer,
  Marker,
  Tooltip as LeafletTooltip,
} from 'react-leaflet';
import Leaflet from 'leaflet';
import Tippy from '@tippyjs/react';
import 'tippy.js/animations/scale.css';

import api from '../../../services/api';
import { useToast } from '../../../hooks/toast';
import { useAnimalFilters } from '../../../hooks/animalFilters';

import {
  IconMapPin,
  IconUserPlus,
  IconMap,
  IconTriangle,
  IconChevronRight,
  IconSearch,
  IconCirclePlus,
  IconNotes,
  IconLock,
} from '@tabler/icons';

import BackHeader from '../../../components/BackHeader';
import Button from '../../../components/Button';
import PropertyTooltip from '../../../components/PropertyTooltip';
import Modal from '../../../components/MapModal';
import { Row, SimpleButton, Tooltip } from '../../../components/Generic';
import {
  Container,
  Content,
  Header,
  Title,
  Description,
  Spec,
  Card,
  UserIcon,
  AnimalCount,
  AnimalCountRow,
} from '../styles';

import mapPinImage from '../../../assets/icons/map-pin.svg';
import ImageCarousel from '../../../components/ImageCarousel';
import userImg from '../../../assets/images/user-avatar.png';

const mapPinIcon = Leaflet.icon({
  iconUrl: mapPinImage,
  iconSize: [50, 50],
  iconAnchor: [25, 50],
  popupAnchor: [0, 0],
});

interface PropertyProps {
  isContributor: boolean;
  property: any;
  owner: any;
}

const Property: React.FC<PropertyProps> = ({
  property,
  owner,
  isContributor,
}) => {
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const history = useHistory();
  const { addToast } = useToast();
  const { animalFilters, setAnimalFilters } = useAnimalFilters();

  const makeRequest = async () => {
    setLoading(true);
    try {
      await api.post(
        `/pedidos-de-colaboracao/`,
        {},
        {
          headers: {
            propriedade_id: property.id,
          },
        },
      );

      addToast({
        type: 'success',
        title: 'Sucesso!',
        description: 'Solicitação enviada.',
      });

      history.push('/inicio');
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: err.response.data.message || 'Algo deu errado!',
      });
    } finally {
      setLoading(false);
    }
  };

  const openMap = () => {
    if (property.latitude && property.longitude) setIsModalOpen(true);
  };

  return (
    <Container>
      <BackHeader title="Propriedade" />

      <Header>
        <ImageCarousel
          data={property.imagens?.length > 0 ? property.imagens : []}
        />
      </Header>

      <Content>
        <Title>
          {property.privada && (
            <IconLock
              size={30}
              color="var(--primary)"
              style={{ marginRight: 10 }}
            />
          )}
          {property.nome}
        </Title>

        <ul className="info-list">
          <li
            onClick={() => openMap()}
            style={{
              cursor:
                property.latitude && property.longitude ? 'pointer' : 'default',
            }}
          >
            <IconMapPin
              size={20}
              color={
                property.latitude && property.longitude
                  ? 'var(--info)'
                  : 'var(--black-7)'
              }
            />
            <span
              style={{
                color:
                  property.latitude && property.longitude
                    ? 'var(--info)'
                    : 'var(--black-7)',
              }}
            >
              {property.descricao}
            </span>
          </li>

          {property.area_total && (
            <li>
              <IconMap size={18} />
              <span>Área total: {property.area_total}ha</span>
            </li>
          )}

          {property.area_produtiva && (
            <li>
              <IconMap size={18} />
              <span>Área produtiva: {property.area_produtiva}ha</span>
            </li>
          )}

          {property.categoria_animais && (
            <li>
              <IconTriangle size={18} />
              <span>Categoria: {property.categoria_animais}</span>
            </li>
          )}
        </ul>

        {!isContributor && (
          <Button
            isLoading={loading}
            onClick={() => makeRequest()}
            style={{ marginTop: 40 }}
          >
            <IconUserPlus size={20} />
            Pedir para colaborar
          </Button>
        )}

        <Card>
          <Row
            style={{
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <label>Colaboradores</label>
          </Row>

          <Row
            style={{
              height: 75,
              overflowX: 'auto',
              overflowY: 'hidden',
              gap: 10,
            }}
          >
            <Tippy
              content={<Tooltip>{owner.nome}</Tooltip>}
              animation="scale"
              placement="bottom"
            >
              <Link to={`/usuario/${owner.id}`}>
                <UserIcon
                  owner={true}
                  src={owner.url || userImg}
                  alt={owner.nome}
                />
              </Link>
            </Tippy>

            {property.colaboradores?.map((user: any) => (
              <Tippy
                content={<Tooltip>{user.usuarios?.nome}</Tooltip>}
                animation="scale"
                placement="bottom"
              >
                <Link to={`/usuario/${user?.usuario_id}`}>
                  <UserIcon src={user.usuarios?.url || userImg} />
                </Link>
              </Tippy>
            ))}
          </Row>
        </Card>

        <Card>
          <Row
            style={{
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <label>Animais</label>
          </Row>

          <AnimalCountRow>
            <button
              onClick={() => {
                setAnimalFilters({
                  estado: 'ativo',
                  sexo: '',
                  ano: '',
                  raça: '',
                  categoria: '',
                  safra: '',
                  lote: '',
                  pertencentes: 'true',
                });

                history.push(`/propriedade/${property.id}/animais/pesquisar`);
              }}
            >
              <AnimalCount>{property?.totalAtivos || 0}</AnimalCount>
              <span>Ativos</span>
            </button>

            {isContributor && (
              <button
                onClick={() => {
                  setAnimalFilters({
                    estado: 'inativo',
                    sexo: '',
                    ano: '',
                    raça: '',
                    categoria: '',
                    safra: '',
                    lote: '',
                    pertencentes: 'true',
                  });

                  history.push(`/propriedade/${property.id}/animais/pesquisar`);
                }}
              >
                <AnimalCount>{property?.totalInativos || 0}</AnimalCount>
                <span>Inativos</span>
              </button>
            )}

            {isContributor && (
              <button
                onClick={() => {
                  setAnimalFilters({
                    estado: 'ativo',
                    sexo: '',
                    ano: '',
                    raça: '',
                    categoria: '',
                    safra: '',
                    lote: '',
                    pertencentes: '',
                  });

                  history.push(`/propriedade/${property.id}/animais/pesquisar`);
                }}
              >
                <AnimalCount>{property?.totalNaoPertencentes || 0}</AnimalCount>
                <span>Externos</span>
              </button>
            )}
          </AnimalCountRow>

          <SimpleButton
            onClick={() =>
              history.push(`/propriedade/${property.id}/animais/pesquisar`)
            }
          >
            <div>
              <IconSearch size={25} style={{ marginRight: 20 }} />
              Pesquisar
            </div>

            <IconChevronRight size={25} />
          </SimpleButton>

          {isContributor && (
            <SimpleButton
              onClick={() =>
                history.push(`/propriedade/${property.id}/animais/novo`)
              }
            >
              <div>
                <IconCirclePlus size={25} style={{ marginRight: 20 }} />
                Adicionar
              </div>

              <IconChevronRight size={25} />
            </SimpleButton>
          )}
        </Card>

        {isContributor && (
          <Card>
            <label>Dados</label>

            <SimpleButton
              onClick={() =>
                history.push(`/propriedade/${property.id}/exportar`)
              }
            >
              <div>
                <IconNotes size={25} style={{ marginRight: 20 }} />
                Relatórios
              </div>

              <IconChevronRight size={25} />
            </SimpleButton>
          </Card>
        )}
      </Content>

      {property.latitude && property.longitude && (
        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          label="Fechar o mapa"
          lat={property.latitude}
          long={property.longitude}
        >
          <MapContainer
            center={[property.latitude, property.longitude]}
            zoom={15}
            scrollWheelZoom={true}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; Contribuidores do <a href="http://osm.org/copyright">OpenStreetMap</a>'
            />

            <Marker
              icon={mapPinIcon}
              position={[property.latitude, property.longitude]}
            >
              <LeafletTooltip
                direction="top"
                offset={[0, -50]}
                opacity={1}
                permanent
              >
                <PropertyTooltip
                  name={property.nome}
                  location={property.descricao}
                />
              </LeafletTooltip>
            </Marker>
          </MapContainer>
        </Modal>
      )}
    </Container>
  );
};

export default Property;
